import styled, { css, keyframes } from 'styled-components';
import { pxToRem } from 'utils/convert';
import {
  colors,
  spacing,
  body2,
  easing,
  duration,
  borderRadius,
  darkOverlayShadow,
} from 'ui/theme';
import { PositiveFill } from 'ui/icons/status/PositiveFill';
import { WarningFill } from 'ui/icons/status/WarningFill';
import { NegativeFill } from 'ui/icons/status/NegativeFill';
import { InfoFill } from 'ui/icons/status/InfoFill';
import { Close16 } from 'ui/icons/action/Close16';
import {
  STANDARD_EASING,
  STANDARD_TIME,
  ENTRANCE_TIME,
  ENTRANCE_EASING,
} from 'constants/animations';
import { TOAST_VARIANTS, VISIBLE_ENUM } from './constants';
import type { ToastPropsType } from './types';

export const TOAST_COLORS = {
  [TOAST_VARIANTS.POSITIVE]: colors.green50,
  [TOAST_VARIANTS.WARNING]: colors.salmon50,
  [TOAST_VARIANTS.NEGATIVE]: colors.red50,
};

const entrance = keyframes`
    from {
      transform: translateY(${spacing.double});
      opacity: 0;
    }
    to {
      transform: translateY(0%);
      opacity: 1;
    }
`;

export const ToastWrapper = styled.div<{ $visibleState: VISIBLE_ENUM }>`
  ${darkOverlayShadow}
  border-radius: ${borderRadius.xl};
  background: ${colors.backgroundDarkOverlay};
  backdrop-filter: blur(${spacing.half});
  margin-bottom: ${spacing.quarter};
  overflow: hidden;
  pointer-events: auto;
  transition: all ${STANDARD_TIME}s ${STANDARD_EASING};
  ${({ $visibleState }) => {
    switch ($visibleState) {
      case VISIBLE_ENUM.ENTERING:
        return css`
          animation: ${entrance} ${ENTRANCE_TIME}s ${ENTRANCE_EASING};
        `;
      case VISIBLE_ENUM.EXITING:
        return css`
          /* Transitions margin-bottom and sets opacity to zero */
          margin-bottom: 0;
          opacity: 0;
        `;
      default:
        return ``;
    }
  }}
`;

// Split from Toast Wrapper to create the flat box shadow that is smaller than border radius
export const ContentWrapper = styled.div<{ $variant: ToastPropsType['variant'] }>`
  display: grid;
  grid-template-columns: ${spacing.double} 1fr ${spacing.double};
  padding: ${spacing.half} ${spacing.half} ${spacing.half} ${spacing.quarter};
`;

export const ContentSection = styled.div`
  display: inline-block;
  overflow-x: hidden;
`;

const ToastIconStyle = css`
  display: inline-flex;
  grid-column: 1 / span 1;
  grid-row: 1 / span 1;
  justify-self: center;
  margin: ${spacing.half} ${spacing.quarter};
`;

export const PositiveIcon = styled(PositiveFill)`
  ${ToastIconStyle}
  path {
    fill: ${colors.green50};
  }
`;

export const WarningIcon = styled(WarningFill)`
  ${ToastIconStyle}
  path {
    fill: ${colors.salmon50};
  }
`;

export const NegativeIcon = styled(NegativeFill)`
  ${ToastIconStyle}
  path {
    fill: ${colors.red50};
  }
`;

export const InfoIcon = styled(InfoFill)`
  ${ToastIconStyle}
  path {
    fill: ${colors.info50};
  } 
`;

export const CloseIcon = styled(Close16)`
  /* Vertical Align removes 3px buffer on bottom */
  vertical-align: top;
  path {
    stroke: ${colors.white};
  }
`;

export const CloseButton = styled.button`
  display: inline-block;

  /* Does not fill width */
  justify-self: center;
  grid-column: 3 / span 1;
  grid-row: 1 / span 1;

  background-color: transparent;
  border: none;
  outline: none;
  transition: background-color ${duration.short} ${easing.standard};

  border-radius: ${spacing.quarter};
  padding: ${spacing.half};
  margin: 0 0 auto 0;

  &:hover,
  &:focus {
    background: rgba(240, 240, 240, 0.16);
    cursor: pointer;
  }

  &:focus {
    box-shadow: inset 0 0 0 ${pxToRem(2)} ${colors.textSecondary};
  }
`;

export const TextSection = styled.div`
  ${body2}
  margin: ${spacing.threeEights} 0;
  color: ${colors.white};
  padding-right: ${spacing.half};
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ActionsSection = styled.div`
  margin-bottom: ${spacing.threeEights};
`;

// This allows the margin to be large and for wrapping without break text
export const ActionWrapper = styled.div`
  display: inline-block;
  margin-right: ${spacing.plusHalf};
`;

export const Action = styled.a`
  ${body2}
  text-decoration: underline;
  padding: ${spacing.single};
  color: ${colors.white};
`;
